<template>
  <section class="shared-table-settings">
    <div class="section-heading">
      <div>
        <p class="section-title">{{ $adminT('adminAll.shared_tables_35b9e3', 'Shared tables') }}</p>
        <p class="section-description">
          {{ $adminT('adminAll.configure_one_existing_table_ticket_type_this_sets_its_tab_73174a', 'Configure one existing table ticket type. This sets its table capacity, permitted packages, ticket inventory, and floor-plan availability in one action.') }}
        </p>
      </div>
    </div>

    <div class="field">
      <label for="shared-table-order-item">{{ $adminT('adminAll.table_ticket_type_9bb407', 'Table ticket type') }}</label>
      <v-select
        id="shared-table-order-item"
        v-model="selectedOrderItem"
        :options="orderItems"
        :filterable="false"
        :loading="searchingOrderItems"
        :get-option-label="getOrderItemLabel"
        :placeholder="$adminT('adminAll.search_by_table_ticket_name_fd676c', 'Search by table ticket name...')"
        @search="searchOrderItems"
      ><template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
      <p class="field-helper">{{ $adminT('adminAll.all_ticket_types_already_marked_as_a_table_are_shown_ab3190', 'All ticket types already marked as a table are shown.') }}</p>
    </div>

    <template v-if="selectedOrderItem">
      <div class="table-summary">
        <strong>{{ selectedOrderItem.name }}</strong>
        <span v-if="selectedTableCount === 1" v-text="$adminT('adminAll.count_linked_floor_plan_table_5dae02', '{{count}} linked floor-plan table', { count: selectedTableCount })" /><span v-else v-text="$adminT('adminAll.count_linked_floor_plan_tables_9bc4e3', '{{count}} linked floor-plan tables', { count: selectedTableCount })" />
      </div>

      <div class="form-grid">
        <div class="field">
          <label for="shared-table-capacity">{{ $adminT('adminAll.seats_per_physical_table_d36994', 'Seats per physical table') }}</label>
          <input
            id="shared-table-capacity"
            v-model.number="capacity"
            min="1"
            step="1"
            type="number"
          />
        </div>
        <div class="field">
          <label for="shared-table-packages">{{ $adminT('adminAll.allowed_packages_5f3735', 'Allowed packages') }}</label>
          <input
            id="shared-table-packages"
            v-model.trim="allowedQuantitiesInput"
            type="text"
            :placeholder="$adminT('adminAll.for_example_2_4_87a010', 'For example: 2, 4')"
          />
          <p class="field-helper">{{ $adminT('adminAll.enter_positive_whole_numbers_separated_by_commas_bfaf5c', 'Enter positive whole numbers, separated by commas.') }}</p>
        </div>
      </div>

      <div class="configuration-preview">
        <span>{{ $adminT('organizationAll.capacity_45bd908d', 'Capacity') }}</span>
        <strong>{{ inventoryPreview }} {{ $adminT('adminAll.tickets_d370d9', 'tickets') }}</strong>
      </div>

      <div v-if="validationError" class="alert alert--error" role="alert">{{ adminAllMessage(validationError) }}</div>
      <div v-if="actionError" class="alert alert--error" role="alert">{{ adminAllMessage(actionError) }}</div>
      <div v-if="successMessage" class="alert alert--success" role="status">{{ adminAllMessage(successMessage) }}</div>

      <main-button
        class=""
        depressed
        :loading="saving"
        :disabled="saving || !!validationError"
        @click.native="configureSharedTable"
      >
        {{ $adminT('adminAll.configure_shared_table_e85315', 'Configure shared table') }}
      </main-button>
    </template>
  </section>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import vSelect from './AdminSelect.vue'
import 'vue-select/dist/vue-select.css'

import {
  CONFIGURE_SHARED_TABLE_SUPERADMIN,
  SEARCH_SHARED_TABLE_ORDER_ITEMS_SUPERADMIN,
} from '@/graphql/admin.graphql'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'shared-table-settings',
  components: {
    'v-select': vSelect,
  },
  props: {
    eventId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      orderItems: [],
      selectedOrderItem: null,
      searchingOrderItems: false,
      saving: false,
      capacity: null,
      allowedQuantitiesInput: '',
      actionError: null,
      successMessage: null,
    }
  },
  computed: {
    selectedTableCount() {
      return (
        (this.selectedOrderItem &&
          this.selectedOrderItem.sectionSeats &&
          this.selectedOrderItem.sectionSeats.length) ||
        0
      )
    },
    allowedQuantities() {
      const values = this.allowedQuantitiesInput
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)

      if (!values.length || values.some(value => !/^\d+$/.test(value))) {
        return []
      }

      return values.map(Number)
    },
    inventoryPreview() {
      return Number.isInteger(this.capacity) && this.capacity > 0
        ? this.capacity * this.selectedTableCount
        : 0
    },
    validationError() {
      if (!this.selectedTableCount)
        return this.$adminT('adminAll.the_selected_ticket_type_has_no_linked_floor_plan_tables_adc014', 'The selected ticket type has no linked floor-plan tables.')
      if (!Number.isInteger(this.capacity) || this.capacity <= 0)
        return this.$adminT('adminAll.capacity_must_be_a_positive_whole_number_5b5270', 'Capacity must be a positive whole number.')
      if (!this.allowedQuantities.length) return this.$adminT('adminAll.enter_at_least_one_allowed_package_b749c0', 'Enter at least one allowed package.')
      if (new Set(this.allowedQuantities).size !== this.allowedQuantities.length) {
        return this.$adminT('adminAll.each_allowed_package_can_be_entered_only_once_480f62', 'Each allowed package can be entered only once.')
      }
      if (this.allowedQuantities.some(quantity => quantity <= 0 || quantity > this.capacity)) {
        return this.$adminT('adminAll.every_allowed_package_must_be_between_1_and_the_table_capa_f4b1c4', 'Every allowed package must be between 1 and the table capacity.')
      }
      if (!this.canFillRemainder(this.capacity, this.allowedQuantities)) {
        return this.$adminT('adminAll.the_table_capacity_must_be_fillable_using_the_allowed_pack_ecaa1b', 'The table capacity must be fillable using the allowed packages.')
      }

      return null
    },
  },
  watch: {
    eventId() {
      this.orderItems = []
      this.selectedOrderItem = null
      this.capacity = null
      this.allowedQuantitiesInput = ''
      this.actionError = null
      this.successMessage = null
      this.loadOrderItems()
    },
    selectedOrderItem(orderItem) {
      this.actionError = null
      this.successMessage = null
      const sharedTable = orderItem && orderItem.itemMeta && orderItem.itemMeta.sharedTable
      this.capacity = sharedTable && sharedTable.capacity ? Number(sharedTable.capacity) : null
      this.allowedQuantitiesInput =
        sharedTable && Array.isArray(sharedTable.allowedQuantities)
          ? sharedTable.allowedQuantities.join(', ')
          : ''
    },
  },
  mounted() {
    this.loadOrderItems()
  },
  methods: {
    canFillRemainder(remaining, allowedQuantities) {
      const fillable = Array(remaining + 1).fill(false)
      fillable[0] = true
      for (let quantity = 1; quantity <= remaining; quantity++) {
        fillable[quantity] = allowedQuantities.some(
          allowed => quantity >= allowed && fillable[quantity - allowed],
        )
      }

      return fillable[remaining]
    },
    getOrderItemLabel(orderItem) {
      if (!orderItem) return ''

      return this.$adminT('adminAll.value1_id_value2_744da8', '{{value1}} · ID {{value2}}', { value1: orderItem.name, value2: orderItem.id })
    },
    async loadOrderItems(query = '') {
      const term = (query || '').trim()

      this.searchingOrderItems = true
      try {
        const { data, errors } = await this.$apollo.query({
          query: SEARCH_SHARED_TABLE_ORDER_ITEMS_SUPERADMIN,
          variables: { eventId: this.eventId, q: term, limit: 50 },
          fetchPolicy: 'network-only',
          errorPolicy: 'none',
        })
        if (errors && errors.length) throw new Error(this.getErrorMessage({ graphQLErrors: errors }, this.adminAllNotice('Could not load table ticket types.')))
        this.orderItems = (data && data.searchSharedTableOrderItemsSuperadmin) || []
      } catch (error) {
        this.orderItems = []
        this.actionError = this.getErrorMessage(error, this.adminAllNotice('Could not load table ticket types.'))
      } finally {
        this.searchingOrderItems = false
      }
    },
    searchOrderItems(query) {
      return this.loadOrderItems(query)
    },
    async configureSharedTable() {
      if (this.validationError || this.saving) return

      this.saving = true
      this.actionError = null
      this.successMessage = null
      try {
        const { data, errors } = await this.$apollo.mutate({
          mutation: CONFIGURE_SHARED_TABLE_SUPERADMIN,
          errorPolicy: 'none',
          variables: {
            input: {
              eventId: this.eventId,
              orderItemId: this.selectedOrderItem.id,
              capacity: this.capacity,
              allowedQuantities: this.allowedQuantities,
            },
          },
        })
        if (errors && errors.length) throw new Error(this.getErrorMessage({ graphQLErrors: errors }, this.adminAllNotice('Could not configure the shared table.')))
        const updatedOrderItem = data && data.configureSharedTableSuperadmin
        if (!updatedOrderItem || String(updatedOrderItem.id) !== String(this.selectedOrderItem.id)) {
          throw new Error()
        }
        this.selectedOrderItem = updatedOrderItem
        this.orderItems = this.orderItems.map(orderItem =>
          String(orderItem.id) === String(updatedOrderItem.id) ? updatedOrderItem : orderItem,
        )
        this.successMessage =
          this.adminAllNotice('Shared table configuration saved. Floor-plan availability and ticket inventory are ready.')
        this.$emit('configured', updatedOrderItem)
      } catch (error) {
        this.actionError = this.getErrorMessage(error, this.adminAllNotice('Could not configure the shared table.'))
      } finally {
        this.saving = false
      }
    },
    getErrorMessage(error, fallback) {
      const networkError = error && error.networkError
      const errors = [
        ...((error && error.graphQLErrors) || []),
        ...((networkError && networkError.result && networkError.result.errors) || []),
      ]
      const messages = errors.map(item => item && item.message)
      messages.push(networkError && networkError.message, error && error.message)

      return messages.find(message => typeof message === 'string' && message.trim() &&
        !/^(?:(?:Network|GraphQL) error:\s*)?(?:undefined|null)?$/i.test(message.trim())) || fallback
    },
  },
}
</script>

<style scoped>
.shared-table-settings {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.section-heading {
  margin-bottom: 1rem;
}

.section-title {
  margin: 0 0 0.35rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-description,
.field-helper {
  margin: 0;
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.field label {
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
}

.field input {
  padding: 0.65rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.table-summary,
.configuration-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.875rem;
}

.table-summary span,
.configuration-preview span {
  color: #64748b;
}

.alert {
  margin: 0 0 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert--error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.alert--success {
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #15803d;
}

@media (max-width: 700px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
